/** The defensive belt (03 §1): searchlights, gun emplacements and proximity charges. */

import type { KitBuilder } from './flora';
import { MeshBuilder, place } from './MeshBuilder';
import { capColor } from './snow';

/** The drum hangs from the yoke tipped this far below the horizontal, lens end down. */
const LAMP_TIP = Math.PI / 2 + 0.35;
const lampFrame = () => place(0, 3.05, -0.27, 0, 1, LAMP_TIP);

/** The part of a searchlight that never moves: plinth and pole. */
export const searchlightBase: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  mb.box(0.7, 0.25, 0.7, { all: p.color('concrete.lo'), top: capColor(p, 'concrete.mid') });
  mb.cylinder(0.1, 0.08, 2.5, 5, p.color('metal.lo'), place(0, 0.25, 0));
  return mb;
};

/**
 * The part that sweeps: yoke and drum, lens to the south (+z) at yaw 0 and tipped down. Its
 * origin is on the ground on the pole's axis, so turning it about y turns the lamp on its pole.
 */
export const searchlightHead: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  mb.box(0.56, 0.12, 0.3, p.color('metal.lo'), place(0, 2.72, 0)); // yoke
  // Housing: a drum whose bright lens is the one face the camera always sees.
  mb.cylinder(0.3, 0.38, 0.52, 8, p.color('metal.mid'), lampFrame(), p.color('fire.3'));
  return mb;
};

/** A disc just proud of the lens, for a self-lit material: what makes the lamp look ON at night. */
export const searchlightLens: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  mb.with(lampFrame(), () => mb.cylinder(0.34, 0.34, 0.03, 8, p.color('fire.3'), place(0, 0.52, 0)));
  return mb;
};

/** Searchlight on a pole, lens to the south and tipped down. The beam itself is an effect (world/SearchlightView). */
export const searchlight: KitBuilder = (p, rng) => {
  const mb = searchlightBase(p, rng);
  mb.merge(searchlightHead(p, rng), place());
  return mb;
};

/** Round concrete turret bunker (mission 11's border belt): LAW or bomb only. Gun to the south. */
export const turretBunker: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  // Turn the ten-sided drum so a flat face, not a corner, looks at the camera.
  const flat = Math.PI / 10;
  mb.cylinder(2.15, 2.0, 1.3, 10, p.color('concrete.mid'), place(0, 0, 0, flat), p.color('concrete.lo'));
  // Overhanging roof ring: its shadow line is what separates drum from roof at 18 px/m.
  mb.cylinder(2.3, 2.3, 0.32, 10, p.color('concrete.hi'), place(0, 1.3, 0, flat), p.color('concrete.mid'));
  // Firing slits on the three faces that can be seen.
  for (const yaw of [-0.628, 0, 0.628]) {
    mb.with(place(0, 0, 0, yaw), () => mb.box(1.0, 0.26, 0.16, p.color('ink.0'), place(0, 0.8, 1.93)));
  }
  // Steel turret: two stacked frusta make a dome at this scale. The GUN is not part of it - it belongs
  // to the crew, and `world/Emplacements` puts it on only while somebody is alive behind the slits.
  mb.cylinder(1.25, 1.05, 0.42, 8, p.color('metal.mid'), place(0, 1.62, 0, Math.PI / 8));
  mb.cylinder(1.05, 0.55, 0.3, 8, p.color('metal.mid'), place(0, 2.04, 0, Math.PI / 8), p.color('metal.hi'));
  if (p.biome === 'arctic') {
    const snow = { all: p.color('ground.2'), top: p.color('ground.3') };
    mb.box(1.2, 0.12, 0.8, snow, place(-1.15, 1.62, -0.95));
    mb.box(0.9, 0.14, 0.7, snow, place(1.25, 1.62, -0.8));
  }
  return mb;
};

/**
 * Manned turret: a ring of steel plate on a concrete pad with a shielded heavy gun on a
 * pintle. Steel and a full ring, where the MG nest is bags and a horseshoe. Falls to grenades.
 */
export const mannedTurret: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  mb.cylinder(1.45, 1.45, 0.16, 10, p.color('concrete.lo'), undefined, p.color('concrete.mid'));
  // Nine plates; the gap is to the north, where the crew climbs in.
  const plates = 9;
  const plate = { all: p.color('metal.mid'), top: capColor(p, 'metal.hi'), back: p.color('metal.lo') };
  for (let i = 0; i < plates; i++) {
    const a = -Math.PI * 0.8 + (i / (plates - 1)) * Math.PI * 1.6;
    mb.box(0.92, 0.8, 0.14, plate, place(Math.sin(a) * 1.25, 0.16, Math.cos(a) * 1.25, a));
  }
  mb.box(0.4, 0.3, 0.4, p.color('ink.1'), place(0, 0.16, -0.5)); // gunner's seat
  mb.cylinder(0.1, 0.08, 0.95, 5, p.color('metal.lo'), place(0, 0.16, 0));
  mb.box(0.3, 0.26, 0.6, p.color('ink.1'), place(0, 1.02, 0.1));
  mb.box(0.12, 0.12, 1.2, p.color('metal.lo'), place(0, 1.1, 0.95));
  mb.box(1.0, 0.55, 0.08, { all: p.color('metal.mid'), top: p.color('metal.hi') }, place(0, 0.86, 0.46)); // gun shield
  return mb;
};

/** Proximity charge on a post (mission 11): the warning-red band is the tell. Shoot it from a distance. */
export const proximityPost: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  mb.box(0.14, 1.5, 0.14, p.color('timber.lo'));
  mb.box(0.42, 0.42, 0.4, { all: p.color('metal.mid'), top: capColor(p, 'metal.hi') }, place(0, 0.86, 0));
  mb.box(0.45, 0.13, 0.43, p.color('enemyAccent.0'), place(0, 1.0, 0));
  mb.box(0.24, 0.12, 0.24, p.color('ink.1'), place(0, 1.5, 0)); // sensor head
  return mb;
};
